// Small shared hooks: unsaved change guard (SHELL-020), online state (SHELL-018), document title,
// media queries.
import { useEffect, useSyncExternalStore } from 'react'
import { useBlocker, type Blocker } from 'react-router'
import { t } from './i18n'

/**
 * Warns before leaving with unsaved work: in app navigation goes through React Router's blocker,
 * tab close and reload through beforeunload. By default a native confirm asks; pass
 * { confirm: false } and render your own dialog from the returned blocker instead.
 */
export function useUnsavedChangesGuard(
  dirty: boolean,
  opts: { message?: string; confirm?: boolean } = {},
): Blocker {
  const message = opts.message ?? t('You have unsaved changes. Leave anyway?')
  const useNativeConfirm = opts.confirm !== false
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      dirty &&
      (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search),
  )

  useEffect(() => {
    if (!useNativeConfirm || blocker.state !== 'blocked') return
    if (window.confirm(message)) blocker.proceed()
    else blocker.reset()
  }, [blocker, message, useNativeConfirm])

  useEffect(() => {
    if (!dirty) return
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      // Older browsers need returnValue set to show the prompt.
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [dirty])

  return blocker
}

function subscribeOnline(cb: () => void): () => void {
  window.addEventListener('online', cb)
  window.addEventListener('offline', cb)
  return () => {
    window.removeEventListener('online', cb)
    window.removeEventListener('offline', cb)
  }
}

/** navigator.onLine as React state. */
export function useOnline(): boolean {
  return useSyncExternalStore(
    subscribeOnline,
    () => navigator.onLine,
    () => true,
  )
}

export const APP_NAME = 'MG AI School'

/** Builds "<Page> · <Community> · MG AI School" (UI.md section 3), skipping empty parts. */
export function documentTitle(parts: ReadonlyArray<string | null | undefined | false>): string {
  return [...parts.filter((p): p is string => typeof p === 'string' && p.trim() !== ''), APP_NAME].join(' · ')
}

/** Sets document.title while mounted: useDocumentTitle([t("Classroom"), community?.name]). */
export function useDocumentTitle(parts: ReadonlyArray<string | null | undefined | false>): void {
  const title = documentTitle(parts)
  useEffect(() => {
    document.title = title
  }, [title])
}

/** Live CSS media query match, e.g. useMediaQuery("(min-width: 1024px)"). */
export function useMediaQuery(query: string): boolean {
  return useSyncExternalStore(
    (cb) => {
      if (typeof window.matchMedia !== 'function') return () => undefined
      const mq = window.matchMedia(query)
      mq.addEventListener?.('change', cb)
      return () => mq.removeEventListener?.('change', cb)
    },
    () => (typeof window.matchMedia === 'function' ? window.matchMedia(query).matches : false),
    () => false,
  )
}

/** Below the md breakpoint (768 px): bottom nav, scrolling tabs. */
export function useIsMobile(): boolean {
  return !useMediaQuery('(min-width: 768px)')
}
